/**
 * The selection rules for the image grids, as one pure function.
 *
 * Kept out of the hook on purpose. The behaviour is a product of four inputs that
 * interact (ctrl x shift x "did the anchor survive" x "did the list change"), which
 * is a lot of branches for something a user notices instantly when it is wrong —
 * so it is written where it can be exercised directly. See utils/selectionModel.test.js.
 *
 * The conventions are the ones every file manager already taught the user:
 *
 *   click                 select just this one
 *   Ctrl / Cmd + click    add or remove this one
 *   Shift + click         the range from the anchor to here, REPLACING the selection
 *   Ctrl + Shift + click  that range, ADDED to the selection
 *
 * Two rules are less obvious and both exist to stop silent surprises:
 *
 *  - A selection belongs to ONE list (`scope`). Clicking inside a different grid
 *    starts fresh there instead of quietly mixing two lists, which would make
 *    "assign all selected to this class" ambiguous.
 *  - Shift keeps the anchor where it is, so the user can sweep the range wider or
 *    narrower. Only click and Ctrl+click move it.
 */

/** Scope key for the unlabeled queue; class grids use their class id. */
export const UNSTRUCTURED_SCOPE = 'unstructured';

/**
 * @param {Object}   input
 * @param {Array}    input.items    - the ordered list the clicked image lives in
 * @param {string}   input.imageId  - the image that was clicked
 * @param {*}        input.listKey  - scope key of that list
 * @param {Set}      input.selected - currently selected ids
 * @param {?string}  input.anchor   - id a Shift range measures from
 * @param {*}        input.scope    - scope the current selection belongs to
 * @param {boolean} [input.ctrl]
 * @param {boolean} [input.shift]
 * @returns {{selected: Set, anchor: ?string, scope: *}}
 */
export const computeSelection = ({
  items = [],
  imageId,
  listKey,
  selected = new Set(),
  anchor = null,
  scope = null,
  ctrl = false,
  shift = false,
}) => {
  // No selection yet means no list to conflict with, so the click adopts its own.
  const sameScope = scope === null || scope === listKey;
  const base = sameScope ? selected : new Set();
  const anchorIndex = sameScope ? items.findIndex((img) => img.id === anchor) : -1;

  let nextSelected;
  let nextAnchor = anchor;

  if (shift && anchorIndex !== -1) {
    const endIndex = items.findIndex((img) => img.id === imageId);
    if (endIndex === -1) {
      // clicked image is not in the list we measured against: treat as a plain click
      return { selected: new Set(), anchor: imageId, scope: null };
    }
    const [min, max] = [Math.min(anchorIndex, endIndex), Math.max(anchorIndex, endIndex)];
    const range = items.slice(min, max + 1).map((img) => img.id);
    nextSelected = ctrl ? new Set([...base, ...range]) : new Set(range);
    // anchor deliberately unchanged so the range can be resized
  } else if (ctrl) {
    nextSelected = new Set(base);
    if (nextSelected.has(imageId)) nextSelected.delete(imageId);
    else nextSelected.add(imageId);
    nextAnchor = imageId;
  } else {
    // A plain click drops the multi-selection: `selectedImage` alone drives the
    // preview, so keeping a one-image Set would badge every tile merely looked at.
    nextSelected = new Set();
    nextAnchor = imageId;
  }

  return {
    selected: nextSelected,
    anchor: nextAnchor,
    // an empty selection owns no list, so the next click anywhere starts clean
    scope: nextSelected.size > 0 ? listKey : null,
  };
};

export default computeSelection;
